import { DOCUMENT, isPlatformBrowser, Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { UserSessionInfo } from '@repo/auth';
import {
  Component,
  inject,
  input,
  makeStateKey,
  PLATFORM_ID,
  TransferState,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { CookieService, GlobalValidator } from '@app/core';
import { TAG_ID } from '@app/core/constants/common.constant';
import { SessionDto } from '@app/core/dto/session.dto';
import { SignUpStep, TrackingEvent } from '@app/core/dto/tracking-data.dto';
import { UserContext } from '@app/core/dto/user-context-cart.dto';
import { CacheStatusEnum } from '@app/core/enums/cache-status.enum';
import { Cookies } from '@app/core/enums/cookie.enum';
import { PopupPositionEnum } from '@app/core/enums/popup-view-type.enum';
import { ApiService } from '@app/core/services/api/api.service';
import { DynamicImportService } from '@app/core/services/dynamic-import/dynamic-import.service';
import { StringUtils } from '@app/core/utils/string.utils';
import { Observable } from 'rxjs';
import { WidthType } from '../../core/enums/width-type.enum';
@Component({
  selector: 'base',
  template: '',
})
export class BaseWidget {
  protected readonly httpClient = inject(HttpClient);
  protected readonly apiService = inject(ApiService);
  protected readonly cookieService = inject(CookieService, { optional: true });
  public readonly autoSuggestSlots = input<{}>();
  public readonly userContextDto = input<UserContext>();
  public readonly inputUserSession = input<SessionDto>();
  public readonly actionResponseContext = input<any>();
  public readonly userSessionInfo = input<UserSessionInfo | null>(null);
  public readonly dyis = inject(DynamicImportService);
  public readonly _dialog = inject(MatDialog);
  private readonly _document: Document = inject(DOCUMENT);
  protected readonly platformId = inject(PLATFORM_ID);
  protected readonly location = inject(Location);
  protected readonly transferState = inject(TransferState);
  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly ua = 'state-user-agent';

  protected hasQueryParams = false;

  constructor() {
    this.hasQueryParams =
      Object.keys(this.activatedRoute.snapshot.queryParams).length != 0;
  }

  getClass(layoutParams: any) {
    if (!layoutParams) {
      return '';
    }
    const cssProperties = [
      { key: 'width', prefix: 'col-span-', suffix: ' ' },
      // { key: 'height', prefix: 'h-[', suffix: '] ' },
      { key: 'orientation', prefix: '', suffix: ' ' },
      { key: 'columnGap', prefix: 'gap-x-', suffix: ' ' },
      // { key: 'borderRadius', prefix: 'rounded-', suffix: ' ' },
      { key: 'fontWeight', prefix: 'font-[', suffix: '] ' },
      { key: 'container', prefix: '', suffix: ' ' },
      { key: 'floatRight', prefix: '', suffix: ' ' },
      { key: 'text', prefix: 'text-[', suffix: '] ' },
      { key: 'borderRadius', prefix: 'rounded-[', suffix: '] ' },
      { key: 'marginContainer', prefix: 'm-', suffix: ' ' },
      { key: 'gap', prefix: 'gap-[', suffix: '] ' },
      { key: 'itemsCenter', prefix: 'items-', suffix: ' ' },
      // { key: 'backgroundColor', prefix: 'bg-[', suffix: '] ' },
      { key: 'highlightBackground', prefix: 'hover:bg-[', suffix: '] ' },
      { key: 'highlightText', prefix: 'hover:text-[', suffix: '] ' },
      { key: 'position', prefix: '', suffix: ' ' },
      { key: 'overflowY', prefix: 'overflow-y-', suffix: ' ' },
      { key: 'justifyContent', prefix: 'justify-', suffix: ' ' },
      { key: 'aspectRatio', prefix: 'aspect-[', suffix: '] ' },
      { key: 'cursor', prefix: 'cursor-', suffix: ' ' },
      { key: 'lineClamp', prefix: 'line-clamp-', suffix: ' ' },
      // STATIC CLASSES START -------------->
      // STATIC CLASSES END --------------->
    ];
    const widthType = layoutParams.widthType;
    const widthIndex = cssProperties.findIndex(
      (property) => property.key === 'width',
    );
    if (widthType != WidthType.FIXED && widthType != WidthType.PERCENTAGE) {
      if (widthIndex !== -1) {
        const { prefix, suffix } = this._getWidthValue(widthType);
        cssProperties[widthIndex].prefix = prefix;
        cssProperties[widthIndex].suffix = suffix;
      }
      const css = cssProperties
        .map((property) => {
          const { key, prefix, suffix } = property;
          return layoutParams[key]
            ? `${prefix}${layoutParams[key]}${suffix}`
            : '';
        })
        .join('');

      return css;
    }
  }

  getStyles(layoutParams: any, widgetAttributes: any = {}) {
    let styles = {};

    for (const key in layoutParams) {
      if (
        (key === 'widgetPrimaryColor' || key === 'backgroundColor') &&
        !layoutParams['highlightBackground']
      ) {
        styles['background-color'] = layoutParams[key];
      }
      if ((key === 'color' || key === 'textColor') && layoutParams[key]) {
        styles['color'] = layoutParams[key];
      }
      if (key === 'margin' && layoutParams[key]) {
        styles['margin'] = this._convertToPxString(layoutParams[key]);
      }
      if (key === 'padding' && layoutParams[key]) {
        styles['padding'] = this._convertToPxString(layoutParams[key]);
      }
      if (key === 'borderBottom' && layoutParams[key]) {
        styles['border-bottom'] = layoutParams[key];
      }
      if (key === 'border' && layoutParams[key]) {
        styles['border'] = layoutParams[key];
      }
      if (key === 'borderRadius' && layoutParams[key]) {
        styles['border-radius'] = layoutParams[key];
      }
      if (key === 'widthType') {
        const width = layoutParams['width'];
        if (width) {
          if (layoutParams[key] == WidthType.PERCENTAGE) {
            styles['width'] = width + '%';
          } else if (layoutParams[key] == WidthType.FIXED) {
            styles['width'] = width;
          } else if (layoutParams[key] == WidthType.PIXEL) {
            styles['width'] = `${width} px`;
          }
        }
      }
      if (key === 'overflowX' && layoutParams[key]) {
        styles['overflow-x'] = layoutParams[key];
      }
      if (key === 'overflowY' && layoutParams[key]) {
        styles['overflow-y'] = layoutParams[key];
      }
      if (key === 'fontSize' && layoutParams[key]) {
        styles['font-size'] = layoutParams[key];
      }
      if (key === 'widthPixel' && layoutParams[key]) {
        styles['width'] = layoutParams[key];
      }
      if (key === 'textAlign' && layoutParams[key]) {
        styles['text-align'] = layoutParams[key];
      }
      if (key === 'backgroundImage' && layoutParams[key]) {
        styles['background-image'] = `url(${layoutParams[key].URL})`;
        styles['background-repeat'] = layoutParams[key].REPEAT;
        styles['background-size'] = layoutParams[key].SIZE;
      }

      if (key === 'paddingBottom' && layoutParams[key]) {
        styles['padding-bottom'] = layoutParams[key];
      }

      if (key === 'height' && layoutParams[key]) {
        styles['height'] = layoutParams[key];
      }

      if (key === 'childHorizontalAlignment' && layoutParams[key]) {
        styles['justify-content'] = layoutParams[key];
      }

      if (key === 'aspectRatio' && layoutParams[key]) {
        styles['aspect-ratio'] = layoutParams[key];
      }
      if (key == 'gradient' && layoutParams[key]) {
        const gradient = `linear-gradient(${layoutParams[key].DIRECTION}, ${layoutParams[key].START_BLEEDING_COLOR}, ${layoutParams[key].STOP_BLEEDING_COLOR})`;
        styles['background-image'] = gradient;
      }

      if (key === 'stickyScroll' && layoutParams[key]) {
        styles['z-index'] = 2;
        styles['position'] = 'sticky';
        // styles['top'] = 0;
      }

      if (key === 'top' && layoutParams[key]) {
        styles['top'] = layoutParams[key];
      }

      if (key === 'bottom' && layoutParams[key]) {
        styles['bottom'] = layoutParams[key];
      }

      if (key === 'left' && layoutParams[key]) {
        styles['left'] = layoutParams[key];
      }

      if (key === 'right' && layoutParams[key]) {
        styles['right'] = layoutParams[key];
      }

      if (key === 'alignItems' && layoutParams[key]) {
        styles['align-items'] = layoutParams[key];
      }

      // for gap
      if (key === 'gap' && layoutParams[key]) {
        styles['gap'] = layoutParams[key];
      }

      if (key === 'zIndex' && layoutParams[key]) {
        styles['z-index'] = layoutParams[key];
      }

      if (key === 'cursor' && layoutParams[key]) {
        styles['cursor'] = layoutParams[key];
      }

      if (key === 'boxShadow' && layoutParams[key]) {
        styles['box-shadow'] = layoutParams[key];
      }
      if (key === 'lineHeight' && layoutParams[key]) {
        styles['line-height'] = layoutParams[key];
      }
      if (key === 'display' && layoutParams[key]) {
        styles['display'] = layoutParams[key];
      }
      if (key === 'justifyContent' && layoutParams[key]) {
        styles['justify-content'] = layoutParams[key];
      }
    }

    return styles;
  }

  private _getWidthValue(widthType: WidthType): {
    prefix: string;
    suffix: string;
  } {
    switch (widthType) {
      case WidthType.COLSPAN:
        return { prefix: 'col-span-', suffix: ' ' };
      case WidthType.FULL_WIDTH:
        return { prefix: 'w-', suffix: ' ' };
      default:
        return { prefix: '', suffix: ' ' };
    }
  }

  private _convertToPxString(data: any) {
    let style = '';
    if (data.includes(',')) {
      style = data
        .split(',')
        .map((number) => `${number}px`)
        .join(' ');
    }
    return style != '' ? style : data;
  }

  async showPopup(
    data: any,
    popupName: string,
    popupPosition: PopupPositionEnum,
  ) {
    // const cores = navigator.hardwareConcurrency;
    // alert(`Cores: ${cores}`);
    this.dyis.showLoadingBar.set(true);
    requestAnimationFrame(() => {
      setTimeout(async () => {
        const popupComponent = await this.dyis.getModalComponent(popupName);
        let dialogRef: MatDialogRef<any> = null;
        if (popupPosition === PopupPositionEnum.BOTTOM) {
          data = {
            ...data,
            panelClass: 'bottom-sheet',
            position: {
              bottom: '0',
              left: '0',
              right: '0',
            },
            maxHeight: '90vh',
            enterAnimationDuration: 0,
            exitAnimationDuration: 0,
          };
          dialogRef = this._dialog.open(popupComponent, data);
        } else {
          data = {
            ...data,
            panelClass:
              popupPosition === PopupPositionEnum.CENTER
                ? 'popup-dialog-rounded'
                : 'popup-dialog',
            enterAnimationDuration: 0,
            exitAnimationDuration: 0,
          };
          dialogRef = this._dialog.open(popupComponent, data);
        }
        this.dyis.showLoadingBar.set(false);
      }, 100);
    });
    // dialogRef.afterClosed().subscribe(async (result: {reload: boolean, pathName: string}) => {
    //   if(result?.reload){
    //     await this._delay(50);
    //     window.location.reload();
    //   }
    //   if(result?.pathName){
    //     window.location.href = result?.pathName;
    //   }

    // })
  }

  private _delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  protected delayOnWorker(delay: number, cb: Function) {
    if (typeof Worker !== 'undefined') {
      const worker = new Worker(new URL('../../app.worker', import.meta.url));
      worker.onmessage = ({ data }) => {
        cb();
      };
      worker.postMessage(delay);
    } else {
      // Web Workers are not supported in this environment.
      // You should add a fallback so that your program still executes correctly.
    }
  }

  protected getUserDetails(body: any): Observable<any> {
    return this.apiService.getUser(body);
  }

  protected goBack() {
    if (isPlatformBrowser(this.platformId)) {
      // Check if there's internal navigation history before going back
      const hasInternalHistory: boolean =
        document.referrer &&
        document.referrer.startsWith(window.location.origin) &&
        window.history.length > 1;
      if (hasInternalHistory) {
        this.location.back();
      } else {
        window.location.href = '/';
      }
    }
  }

  protected getTrackingData(action, value?: string) {
    if (!action?.tracking?.event) {
      return;
    }

    let trackingParams: any = {};

    switch (action.tracking.event) {
      case TrackingEvent.IBCREDIT_GST:
        if (GlobalValidator.GSTIN_REGEX.test(value)) {
          trackingParams.gstin = value;
        }
        break;

      case TrackingEvent.IBCREDIT_MOBILE:
        if (GlobalValidator.PHONE_REGEX.test(value)) {
          trackingParams.user_phone = value;
        }
        break;

      case TrackingEvent.IBCREDIT_EMAIL:
        if (StringUtils.isValidEmail(value)) {
          trackingParams.user_email = value;
        }
        break;

      case TrackingEvent.SIGNUP_PROGRESS:
        if (
          action.tracking.auth_step === SignUpStep.SIGNUP_GSTIN_VERIFICATION
        ) {
          trackingParams.gstin = value;
        }
        break;
    }

    if (Object.keys(trackingParams).length) {
      action.tracking = {
        ...action.tracking,
        ...trackingParams,
      };
      return true;
    } else {
      return false;
    }
  }

  protected shouldSkipHydration(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const botKeywords: string[] = [
        'lighthouse',
        'bot',
        'google',
        'k6',
        'frog',
        'rush',
      ];
      const uaKey = makeStateKey<string>(String(this.ua));
      if (this.transferState.hasKey(uaKey)) {
        const ua = this.transferState.get(uaKey, '')?.toLowerCase() || '';
        return botKeywords.some((keyword) => ua.includes(keyword));
      }
    }
    return true;
    // let serverStateData: any = this._getServerStateData();
    // const skipHydration = serverStateData?.skipHydration ?? false;
    // return skipHydration;
  }

  protected getCacheStatus(): CacheStatusEnum {
    return CacheStatusEnum.MISS;
    // let serverStateData: any = this._getServerStateData();
    // const cacheStatus: CacheStatusEnum =
    //   serverStateData?.['x-cache-status'] ?? CacheStatusEnum.MISS;
    // return cacheStatus;
  }

  private _getServerStateData(): any {
    const serverTransferStateScript =
      this._document.querySelectorAll('#ng-state-server');
    const data: any = serverTransferStateScript?.[0]?.innerHTML;
    if (data) {
      return JSON.parse(data);
    }
    return null;
  }

  updateInputQuantity(event: any, action: any, minQuantity: number): number {
    if (!minQuantity) {
      minQuantity = 1;
    }

    const inputElement: HTMLInputElement = event.target as HTMLInputElement;
    const quantity: number = parseInt(inputElement.value) || minQuantity; // Default to minQuantity if NaN or zero
    inputElement.value = quantity.toString(); // update input box
    action.params.quantity = quantity; // update action parameter
    return quantity;
  }

  private _cloudflareHeadersFetch: Promise<void> | null = null;

  removeStaticContent() {
    const existingTag: HTMLElement = this._document.getElementById(TAG_ID);
    if (existingTag) {
      existingTag.remove();
    }
  }

  /**
   * Stores the visitor's pincode and country (cookies, 365 days) from the
   * location API. One in-flight/finished call per instance, so callers can
   * await it without triggering another request. Never rejects.
   */
  protected fetchCloudflareHeaders(): Promise<void> {
    this._cloudflareHeadersFetch ??= this._loadCloudflareHeaders();
    return this._cloudflareHeadersFetch;
  }

  private async _loadCloudflareHeaders(): Promise<void> {
    const hasPincode: boolean = StringUtils.isValidPincode(
      this.cookieService?.getCookie(Cookies.CF_POSTAL_CODE),
    );
    const hasCountry: boolean = !!this.cookieService?.getCookie(
      Cookies.CF_COUNTRY,
    );
    if (hasPincode && hasCountry) {
      return;
    }

    try {
      const res: any = await this.apiService.getCloudflareHeaders();
      if (!hasPincode && StringUtils.isValidPincode(res?.postalCode)) {
        this.cookieService?.setCookie(
          Cookies.CF_POSTAL_CODE,
          res?.postalCode,
          365,
        );
      }
      if (!hasCountry && /^[A-Za-z]{2}$/.test(res?.country ?? '')) {
        this.cookieService?.setCookie(
          Cookies.CF_COUNTRY,
          res.country.toUpperCase(),
          365,
        );
      }
    } catch {
      // Location is best-effort; callers fall back to their own defaults.
    }
  }

  protected _postMessage(data: Record<string, any>) {
    // window.parent.postMessage({message: data},"*");
  }

  protected _stopPropagation($event?: Event) {
    $event?.stopPropagation();
    $event?.preventDefault();
  }

  closePopupOnRedirection() {
    this._dialog?.closeAll();
  }
}

export enum cssValues {
  width = 'width',
}
